import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import styled from "styled-components";

const CheckList = styled.div`
	position: relative;
	display: grid;
	grid-template-columns: 30px auto;
	align-items: center;
	justify-content: center;
	pointer-events: ${(props) => (props.checkable ? "auto" : "none")};
	-webkit-user-select: none;
	user-select: none;

	label {
		color: var(--color-on-surface);
		position: relative;
		cursor: pointer;
		display: grid;
		align-items: center;
		width: fit-content;
		transition: color 0.3s ease;
		margin-right: 20px;
	}

	label::before,
	label::after {
		content: "";
		position: absolute;
	}

	label::before {
		height: 2px;
		width: 8px;
		left: -27px;
		background: var(--color-secondary);
		border-radius: 2px;
		transition: background 0.3s ease;
	}

	label:after {
		height: 4px;
		width: 4px;
		top: 8px;
		left: -25px;
		border-radius: 50%;
	}

	input[type="checkbox"] {
		-webkit-appearance: none;
		-moz-appearance: none;
		position: relative;
		height: 15px;
		width: 15px;
		outline: none;
		border: 0;
		margin: 0 15px 0 0;
		cursor: pointer;
		display: grid;
		align-items: center;
		margin-right: 20px;
	}

	input[type="checkbox"]::before,
	input[type="checkbox"]::after {
		content: "";
		position: absolute;
		height: 2px;
		top: auto;
		background: var(--color-secondary);
		border-radius: 2px;
	}

	input[type="checkbox"]::before {
		width: 0px;
		right: 60%;
		transform-origin: right bottom;
	}

	input[type="checkbox"]::after {
		width: 0px;
		left: 40%;
		transform-origin: left bottom;
	}

	input[type="checkbox"]:checked::before {
		animation: check-01 0.4s ease forwards;
	}

	input[type="checkbox"]:checked::after {
		animation: check-02 0.4s ease forwards;
	}

	input[type="checkbox"]:checked + label {
		color: #c3c8de;
		animation: move 0.3s ease 0.1s forwards;
	}

	input[type="checkbox"]:checked + label::before {
		background: #c3c8de;
		animation: slice 0.4s ease forwards;
	}

	input[type="checkbox"]:checked + label::after {
		animation: firework 0.5s ease forwards 0.1s;
	}

	@keyframes move {
		50% {
			padding-left: 8px;
			padding-right: 0px;
		}

		100% {
			padding-right: 4px;
		}
	}

	@keyframes slice {
		60% {
			width: 100%;
			left: 4px;
		}

		100% {
			width: 100%;
			left: -2px;
			padding-left: 0;
		}
	}

	@keyframes check-01 {
		0% {
			width: 4px;
			top: auto;
			transform: rotate(0);
		}

		50% {
			width: 0px;
			top: auto;
			transform: rotate(0);
		}

		51% {
			width: 0px;
			top: 8px;
			transform: rotate(45deg);
		}

		100% {
			width: 5px;
			top: 8px;
			transform: rotate(45deg);
		}
	}

	@keyframes check-02 {
		0% {
			width: 4px;
			top: auto;
			transform: rotate(0);
		}

		50% {
			width: 0px;
			top: auto;
			transform: rotate(0);
		}

		51% {
			width: 0px;
			top: 8px;
			transform: rotate(-45deg);
		}

		100% {
			width: 10px;
			top: 8px;
			transform: rotate(-45deg);
		}
	}

	@keyframes firework {
		0% {
			opacity: 1;
			box-shadow: 0 0 0 -2px var(--color-secondary),
				0 0 0 -2px var(--color-secondary),
				0 0 0 -2px var(--color-secondary),
				0 0 0 -2px var(--color-secondary),
				0 0 0 -2px var(--color-secondary),
				0 0 0 -2px var(--color-secondary);
		}

		30% {
			opacity: 1;
		}

		100% {
			opacity: 0;
			box-shadow: 0 -15px 0 0px var(--color-secondary),
				14px -8px 0 0px var(--color-secondary),
				14px 8px 0 0px var(--color-secondary),
				0 15px 0 0px var(--color-secondary),
				-14px 8px 0 0px var(--color-secondary),
				-14px -8px 0 0px var(--color-secondary);
		}
	}
`;

const CheckListItem = forwardRef(
	(
		{
			id = "",
			name = "",
			isChecked = false,
			label = "",
			checkable = true,
			onCheckChange = () => {},
		},
		ref
	) => {
		const checkRef = useRef(null);

		const onCheckChangeHandler = () => {
			if (onCheckChange) {
				onCheckChange(checkRef.current.checked);
			}
		};

		useImperativeHandle(ref, () => ({
			check: () => {
				checkRef.current.checked = true;
			},
			uncheck: () => {
				checkRef.current.checked = false;
			},
			toggle: () => {
				checkRef.current.checked = !checkRef.current.checked;
			},
			isChecked: () => checkRef.current,
		}));

		useEffect(() => {
			checkRef.current.checked = isChecked;
		}, [isChecked]);

		return (
			<CheckList checkable={checkable}>
				<input
					ref={checkRef}
					defaultChecked={isChecked}
					name={name}
					type="checkbox"
					id={id}
					onChange={onCheckChangeHandler}
				/>
				<label htmlFor={id}>{label}</label>
			</CheckList>
		);
	}
);

export default CheckListItem;
